// File: typescript/apps/front-end/src/lib/domReconciliationGuard.ts
//
// Defensive guards against the React-vs-third-party-DOM-mutation crash (facebook/react#11538).
//
// A browser extension — overwhelmingly Google Translate, which replaces text nodes with <font>
// wrappers in place — mutates DOM that a React island owns. React keeps a fiber reference to the
// ORIGINAL node, so when it later commits a deletion/move it calls `parent.removeChild(originalNode)`
// (or `parent.insertBefore(node, referenceNode)`) against a node that is no longer a child of
// `parent`. The DOM throws `NotFoundError: ... not a child of this node`, which escapes React's commit
// phase and tears down the entire island (seen in prod as a MountErrorBoundary crash on
// /create/discussion, where the large translatable TipTap editor is a likely trigger surface).
//
// These guards delegate to the native methods in every normal case and only diverge on the
// already-broken cross-parent anomaly, where the native call would throw anyway: instead of throwing
// we no-op and return the node, so React's commit completes and the island survives. This keeps Google
// Translate fully working for our multilingual audience (we deliberately do NOT mark the app
// `notranslate`). See designs/04-live/2026-06-21-frontend-third-party-dom-mutation-removechild-crash-guard-design.md.

import logger from "@/lib/logger";

type RemoveChildFn = <T extends Node>(child: T) => T;
type InsertBeforeFn = <T extends Node>(node: T, child: Node | null) => T;

let installed = false;
let originalRemoveChild: RemoveChildFn | null = null;
let originalInsertBefore: InsertBeforeFn | null = null;

// Re-entrancy + spam control. The guard runs inside React's synchronous commit and can trip many
// times in a burst; we log the first trip of each kind (the logger additionally dedupes by message
// over a 30s window) and suppress logging while a report is in flight so the logging path can never
// recurse back through the patched methods.
const reportedKinds = new Set<string>();
let reporting = false;

function reportGuardTrip(operation: "removeChild" | "insertBefore"): void {
    if (reporting || reportedKinds.has(operation)) {
        return;
    }
    reportedKinds.add(operation);
    reporting = true;
    try {
        const path = typeof location !== "undefined" ? location.pathname : "";
        logger.warn(
            `domReconciliationGuard: suppressed cross-parent ${operation} ` +
                `(third-party DOM mutation, likely Google Translate) at ${path}`,
        );
    } catch {
        // never let telemetry break the guard
    } finally {
        reporting = false;
    }
}

export function installDomReconciliationGuards(): void {
    if (installed || typeof Node === "undefined" || !Node.prototype) {
        return;
    }
    installed = true;

    originalRemoveChild = Node.prototype.removeChild as RemoveChildFn;
    originalInsertBefore = Node.prototype.insertBefore as InsertBeforeFn;

    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            reportGuardTrip("removeChild");
            return child;
        }
        return (originalRemoveChild as RemoveChildFn).call(this, child) as T;
    } as typeof Node.prototype.removeChild;

    Node.prototype.insertBefore = function <T extends Node>(
        this: Node,
        node: T,
        referenceNode: Node | null,
    ): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            reportGuardTrip("insertBefore");
            return node;
        }
        return (originalInsertBefore as InsertBeforeFn).call(this, node, referenceNode) as T;
    } as typeof Node.prototype.insertBefore;
}

export function resetDomReconciliationGuardsForTests(): void {
    if (originalRemoveChild) {
        Node.prototype.removeChild = originalRemoveChild as typeof Node.prototype.removeChild;
    }
    if (originalInsertBefore) {
        Node.prototype.insertBefore = originalInsertBefore as typeof Node.prototype.insertBefore;
    }
    installed = false;
    originalRemoveChild = null;
    originalInsertBefore = null;
    reportedKinds.clear();
    reporting = false;
}
